import { useCallback, useMemo, useState } from 'react';
import { AmazonGiftCardModal, TransactionHistoryModal } from './modals';
import { PrepaidCardHelpModal } from './modals/PrepaidCardHelpModal/PrepaidCardHelpModal';
import TransactionTable from './TransactionTable/TransactionTable';
import { CancelTransactionModal } from './modals/CancelTransactionModal/CancelTransactionModal';
import { IAccount } from '@/types/types/forms/wallet/wallet.interface';

interface ModalState {
    isOpen: boolean;
    selectedTransactionId: number | undefined;
}

type ModalType = keyof ModalsState;
interface amazonGiftModalState extends ModalState {
    selectedTransactionMethodId: number | undefined;
}

interface ModalsState {
    amazonGiftCard: amazonGiftModalState;
    transactionHistory: ModalState;
    prepaidCardHelp: ModalState;
    cancelTransaction: ModalState;
}

const defaultModalState = {
    isOpen: false,
    selectedTransactionId: undefined,
};

const amazonCardDefaultModalState = {
    ...defaultModalState,
    selectedTransactionMethodId: undefined,
};

const initialModalsState: ModalsState = {
    amazonGiftCard: amazonCardDefaultModalState,
    transactionHistory: defaultModalState,
    prepaidCardHelp: defaultModalState,
    cancelTransaction: defaultModalState,
};

interface TransactionTableSectionProps {
    selectedAccount: IAccount;
    participationKey: string | undefined;
    showArchivedTransactions: boolean;
    setShowArchivedTransactions: (showArchivedTransactions: boolean) => void;
}

export const TransactionTableSection = (props: TransactionTableSectionProps) => {
    const [modalState, setModalState] = useState<ModalsState>(initialModalsState);

    const handleModalOpen =
        (modalType: ModalType) => (transactionId: number, methodId?: number) => {
            const baseModalState = {
                ...initialModalsState,
                [modalType]: {
                    isOpen: true,
                    selectedTransactionId: transactionId,
                },
            };

            if (modalType === 'amazonGiftCard' && methodId !== undefined) {
                baseModalState[modalType] = {
                    ...baseModalState[modalType],
                    selectedTransactionMethodId: methodId,
                };
            }

            setModalState(baseModalState);
        };

    const handleModalClose = useCallback(() => {
        setModalState(initialModalsState);
    }, []);

    const handleTransactionHistoryModalOpen = useMemo(
        () => handleModalOpen('transactionHistory'),
        [],
    );
    const handleCancelTransactionModalOpen = useMemo(
        () => handleModalOpen('cancelTransaction'),
        [],
    );
    const handleAmazonGiftCardModalOpen = useMemo(() => handleModalOpen('amazonGiftCard'), []);
    const handlePrepaidCardHelpModalOpen = useMemo(() => handleModalOpen('prepaidCardHelp'), []);

    return (
        <>
            <TransactionTable
                handleStatusClicked={handleTransactionHistoryModalOpen}
                handleDisplayAmazonGiftCardClicked={handleAmazonGiftCardModalOpen}
                handlePrepaidCardHelpClicked={handlePrepaidCardHelpModalOpen}
                handleCancelTransactionClicked={handleCancelTransactionModalOpen}
                setShowArchivedTransactions={props.setShowArchivedTransactions}
                showArchivedTransactions={props.showArchivedTransactions}
                participationKey={props.participationKey}
                selectedAccount={props.selectedAccount}
            />
            <TransactionHistoryModal
                isOpen={modalState.transactionHistory.isOpen}
                handleClose={handleModalClose}
                transactionId={modalState.transactionHistory.selectedTransactionId}
            />
            <AmazonGiftCardModal
                isOpen={modalState.amazonGiftCard.isOpen}
                handleClose={handleModalClose}
                transactionId={modalState.amazonGiftCard.selectedTransactionId}
                transactionMethodId={modalState.amazonGiftCard.selectedTransactionMethodId}
                selectedAccount={props.selectedAccount}
            />
            <PrepaidCardHelpModal
                isOpen={modalState.prepaidCardHelp.isOpen}
                handleClose={handleModalClose}
            />
            <CancelTransactionModal
                isOpen={modalState.cancelTransaction.isOpen}
                handleClose={handleModalClose}
                accountId={props.selectedAccount.accountId}
                transactionId={modalState.cancelTransaction.selectedTransactionId}
                archivedTransactions={props.showArchivedTransactions}
            />
        </>
    );
};
